import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import parser from 'react-html-parser'
import useStaticData from '../../../common/useStaticData'
import { useColumnsCI } from '../../../common/tableHeaders'
import TableView from '../../../components/TableView'

const CrackInjectionPrintable = () => {
  const { input, output } = useSelector((state) => state.calculation)
  const staticData = useSelector((state) => state.staticData)

  const { injectionResin, pasteOverEpoxy, pumpingPortStyle } = input
  const {
    injectionResinResults,
    pasteOverEpoxyResults,
    pumpingPortsResults,
    injectionFittingsResults,
  } = output

  const { subAppList, crackInjectionOptions } = staticData

  const { getInjectionResinCrackWidthList, getCrackInjectionFootnotes, getProductImages } =
    useStaticData(staticData)
  const {
    headerInjectionResin,
    headerPasteOverEpoxy,
    headerPumpingPorts,
    headerInjectionFittings,
  } = useColumnsCI()

  // const subAppItem = subAppList.find((p) => p.value === input.subApp)
  // const portStyle = crackInjectionOptions.pumpingPortStyle.find(
  //   (option) => option.value === input.pumpingPortStyle,
  // )
  const relatedImageItems = [pasteOverEpoxy !== 'none' ? pumpingPortStyle : ''].concat([
    'CIMixingNozzles',
    'CIDispensingTools',
  ])
  const relatedImages = getProductImages(relatedImageItems)

  const installationColumns = useMemo(() => {
    const columns = [
      {
        Header: 'Average Crack Width',
        accessor: (data) =>
          crackInjectionOptions.crackWidth.find((option) => option.value === data.crackWidth).text,
      },
      {
        Header: 'Crack Depth (in)',
        accessor: 'crackDepth',
      },
      {
        Header: 'Crack Length (ft)',
        accessor: 'crackLength',
      },
      {
        Header: 'Overage Factor',
        accessor: (data) =>
          crackInjectionOptions.overageFactor.find((option) => option.value === data.overageFactor)
            .text,
      },
    ]

    return columns
  }, [crackInjectionOptions.crackWidth, crackInjectionOptions.overageFactor])

  const productSelectionsColumns = useMemo(() => {
    const injectionResinOptions = getInjectionResinCrackWidthList(input.crackWidth)

    const columns = [
      {
        Header: 'Injection Resin',
        accessor: (data) => {
          const injectionResinText = injectionResinOptions.find(
            (option) => option.value === data.injectionResin,
          ).text
          return (
            <span>
              {data.injectionResin}
              <br />
              {injectionResinText}
            </span>
          )
        },
      },
      {
        Header: 'Paste-Over Epoxy',
        accessor: (data) => {
          const pasteOverEpoxyText = crackInjectionOptions.pasteOverEpoxy.find(
            (option) => option.value === data.pasteOverEpoxy,
          ).text
          return (
            <span>
              {data.pasteOverEpoxy}
              <br />
              {pasteOverEpoxyText}
            </span>
          )
        },
      },
      {
        Header: 'Pumping Port Style',
        accessor: 'pumpingPortStyle',
      },
      {
        Header: 'Pumping Port Spacing',
        accessor: 'pumpingPortSpacing',
      },
    ]

    return input.pasteOverEpoxy !== 'none' ? columns : columns.slice(0, -2)
  }, [
    getInjectionResinCrackWidthList,
    crackInjectionOptions.pasteOverEpoxy,
    input.crackWidth,
    input.pasteOverEpoxy,
  ])

  const tableWithNotes = useMemo(() => {
    const fittingModels = injectionFittingsResults.reduce(
      (result, item) => result.concat([item.model]),
      [],
    )
    const pumpingPortModels = pumpingPortsResults.reduce(
      (result, item) => result.concat([item.model]),
      [],
    )

    const injectionResinNotes = getCrackInjectionFootnotes([injectionResin])
    const pasteOverEpoxyNotes =
      pasteOverEpoxy !== 'none' ? getCrackInjectionFootnotes([pasteOverEpoxy]) : []
    const pumpingPortNotes =
      pasteOverEpoxy !== 'none'
        ? getCrackInjectionFootnotes(pumpingPortModels.concat([pumpingPortStyle]))
        : []
    const injectionFittingsNotes =
      pasteOverEpoxy !== 'none' ? getCrackInjectionFootnotes(fittingModels) : []

    const modelColumn = [
      {
        Header: 'Model',
        accessor: 'model',
        Cell: (row) => (
          <>
            <a
              name={row.cell.value}
              href={row.row.original.productUrl || 'TBD'}
              target="_blank"
              onClick={(e) => handleAnchorClick(e, row)}
            >
              {row.cell.value}
            </a>
          </>
        ),
      },
    ]

    const dataset = [
      {
        title: 'Injection Resin',
        columns: modelColumn.concat(headerInjectionResin),
        data: injectionResinResults,
        notes: injectionResinNotes,
      },
      {
        title: 'Paste-Over Epoxy',
        columns: modelColumn.concat(headerPasteOverEpoxy),
        data: pasteOverEpoxyResults,
        notes: pasteOverEpoxyNotes,
      },
      {
        title: 'Pumping Ports',
        columns: modelColumn.concat(headerPumpingPorts),
        data: pumpingPortsResults,
        notes: pumpingPortNotes,
      },
      {
        title: 'Injection Fittings',
        columns: modelColumn.concat(headerInjectionFittings),
        data: injectionFittingsResults,
        notes: injectionFittingsNotes,
      },
    ]

    return pasteOverEpoxy !== 'none' ? dataset : dataset.slice(0, 1)
  }, [
    injectionResinResults,
    pasteOverEpoxyResults,
    pumpingPortsResults,
    injectionFittingsResults,
    injectionResin,
    pasteOverEpoxy,
    pumpingPortStyle,
  ])

  return (
    <div>
      {input && (
        <section>
          <h2>Input</h2>

          {/*
        <section>
          <h3>Condition</h3>
          <div className="products-illustration">
            {subAppItem && <figure>
              <img src={subAppItem.imgSrc} alt={subAppItem.text} />
              <figcaption>{subAppItem.text}</figcaption>
            </figure>}

            {(input.pasteOverEpoxy !== 'none' && portStyle) && <figure>
              <img src={portStyle.imgSrc} alt={portStyle.text} />
              <figcaption>{portStyle.text}</figcaption>
            </figure>}
          </div>
        </section>
        */}

          <TableView title={'Installation'} columns={installationColumns} data={[input]} />

          <TableView
            title={'Product Selections'}
            columns={productSelectionsColumns}
            data={[input]}
          />

          {input.pasteOverEpoxy !== 'none' && (
            <div className="formGroup">
              <label className="formGroup__title">Paste-Over Both Sides Of Crack</label>
              <input type="checkbox" checked={input.pasteOverBothSidesOfCrack} readOnly />
              <label>
                <span>Yes, seal back of crack</span>
              </label>
            </div>
          )}
        </section>
      )}

      <div className="page-break" />

      {output && (
        <section>
          <h2>Output</h2>

          {tableWithNotes &&
            tableWithNotes.length > 0 &&
            tableWithNotes.map((item, idx) => {
              const { notes, ...rest } = item

              return (
                <section key={`${item.title}-${idx}`}>
                  <TableView {...rest} />
                  {notes && notes.length > 0 && (
                    <section>
                      <ul>{parser(notes.join('').replace(/\\/g, ''))}</ul>
                    </section>
                  )}
                </section>
              )
            })}

          <section>
            <h3>Related Products</h3>
            {relatedImages &&
              relatedImages.length > 0 &&
              relatedImages.map((item) => (
                <a
                  className="relatedProduct"
                  href={item.productUrl}
                  target="_blank"
                  key={item.title}
                  title={item.title}
                >
                  <img src={item.imageUrl} alt={item.alt} />
                  <div className="relatedProduct__name">{item.title}</div>
                </a>
              ))}
          </section>
        </section>
      )}
    </div>
  )
}

export default CrackInjectionPrintable
